import i18nFactory from '@yandex-int/i18n';
import { Image, Spin } from 'lego-on-react';
import React from 'react';

import { Loader } from '../../components/Loader/Loader';
import { Shape2, Shape2Icon, Shape2Tone } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';
import { CanvasEditableLabelContainer } from '../CanvasEditableLabel/CanvasEditableLabelContainer';
import { CanvasFooter } from '../CanvasFooter/CanvasFooter';
import { Html5Dropzone } from '../Html5Dropzone/Html5Dropzone';
import { Html5UploadingFiles } from '../Html5UploadingFiles/Html5UploadingFiles';

import './Html5Upload.css';
import * as keyset from './Html5Upload.i18n';
import { UploadCreative, UploadStatus } from './typings';

const i18n = i18nFactory(keyset);

const BLOCK = 'html5-upload';

export type Html5UploadProps = {
    isBusy: boolean;
    name: string;
    creatives: UploadCreative[];
    helpUrl: string;
    cancelUpload: (creative: UploadCreative) => void;
    upload: (creatives: UploadCreative[]) => void;
    submit: () => void;
    cancel: () => void;
    hideError: () => void;
    error: { message: string } | undefined;
    saveUploadName: (name: string) => void;
};

class LeftPanePreview extends React.PureComponent<{
    creative: UploadCreative;
    delete: (creative: UploadCreative) => void;
}> {
    render() {
        const { creative } = this.props;
        const isProgress = creative.status === UploadStatus.PROGRESS;

        return (
            <div className={Bem.cls(BLOCK, 'left-pane-item')}>
                <Image cls={Bem.cls(BLOCK, 'left-pane-image')} url={creative.thumbnailUrl} width={120} />
                {isProgress ? (
                    <Spin progress={true} size={'s'} cls={Bem.cls(BLOCK, 'left-pane-spin')} />
                ) : (
                    <>
                        {`${creative.width}x${creative.height}`}
                        <Shape2
                            cls={Bem.cls(BLOCK, 'left-pane-item-trash')}
                            icon={Shape2Icon.trashbin}
                            width={12}
                            height={12}
                            onClick={this.onDelete}
                            tone={Shape2Tone.darkGrey}
                            hoverable={true}
                        />
                    </>
                )}
            </div>
        );
    }

    private onDelete = () => {
        this.props.delete(this.props.creative);
    };
}

export class Html5Upload extends React.PureComponent<Html5UploadProps> {
    static NEXT_FILE_ID = 0;
    private dropzone: React.RefObject<Html5Dropzone> & React.RefObject<HTMLDivElement> = React.createRef();

    componentDidMount(): void {
        this.props.saveUploadName(i18n('new-creative'));
    }

    render() {
        const { creatives, helpUrl, name } = this.props;
        const progressItems = creatives.filter(file => file.status === UploadStatus.PROGRESS);
        const successItems = creatives.filter(file => file.status === UploadStatus.SUCCESS);
        const errorItems = creatives.filter(file => file.status === UploadStatus.ERROR);
        const progressAndSuccess = progressItems.concat(successItems);
        const submitEnabled = successItems.length > 0;

        return (
            <div className={BLOCK}>
                {creatives.length > 0 && (
                    <div className={Bem.cls(BLOCK, 'left-pane')}>
                        {progressAndSuccess.map((file, index) => {
                            return (
                                <div key={index.toString()}>
                                    <LeftPanePreview creative={file} delete={this.props.cancelUpload} />
                                </div>
                            );
                        })}
                    </div>
                )}
                <div
                    className={Bem.cls(BLOCK, 'wrapper', {
                        files: creatives.length > 0 ? 'yes' : ''
                    })}
                >
                    <CanvasEditableLabelContainer
                        savedValue={name}
                        onValueSaved={this.onSaveName}
                        editorId={'NEW_CREATIVE_NAME'}
                        hasClear={false}
                        enableEmptyInput={false}
                        withControls={true}
                        editBtnCls={Bem.cls(BLOCK, 'edit-btn')}
                        cls={Bem.cls(BLOCK, 'creative-name')}
                    />
                    <div className={Bem.cls(BLOCK, 'uploads')}>
                        <Html5Dropzone
                            ref={this.dropzone}
                            hasCreatives={creatives.length > 0}
                            filesSelected={this.onFilesSelected}
                            helpUrl={helpUrl}
                        />
                        {creatives.length > 0 && (
                            <Html5UploadingFiles
                                progressItems={progressItems}
                                successItems={successItems}
                                errorItems={errorItems}
                                delete={this.props.cancelUpload}
                                repeat={this.onRepeat}
                            />
                        )}
                    </div>
                </div>

                <CanvasFooter
                    cls={Bem.cls(BLOCK, 'footer')}
                    submitEnabled={submitEnabled}
                    submitVisible={true}
                    submitLabel={i18n('create')}
                    cancelLabel={i18n('cancel')}
                    error={this.props.error}
                    hideError={this.props.hideError}
                    submit={this.props.submit}
                    cancel={this.props.cancel}
                />
                {this.props.isBusy && <Loader size={'s'} />}
            </div>
        );
    }

    private onRepeat = () => {
        if (this.dropzone && this.dropzone.current) {
            this.dropzone.current.upload();
        }
    };

    private onFilesSelected = (files: File[]) => {
        this.props.upload(
            files.map(file => ({
                fileId: ++Html5Upload.NEXT_FILE_ID,
                file,
                name: file.name,
                status: UploadStatus.PROGRESS
            }))
        );
    };

    private onSaveName = (val: string) => {
        this.props.saveUploadName(val);
    };
}
